// THE ATTACHMENT RULES THE BROWSER APPLIES BEFORE IT SENDS ANYTHING (spec 09
// §2.29 and §4.2's E2-2a2 annotation, D-323 rulings 8 to 12, D-335 rulings 1
// and 2). Pure, no React, unit-tested in
// __tests__/attachment-model.test.ts.
//
// THE TYPE LIST IS THE CONTRACT'S, RESTATED. The upload dialog offers exactly
// the types the `sales_attachment` purpose stores
// (`SALES_ATTACHMENT_CONTENT_TYPES`, packages/contracts/src/files/schemas.ts):
// the office document types and an AutoCAD drawing, stored as
// `application/acad`. The web does not import the contracts index, which
// would carry zod and the OpenAPI registry into the office bundle, so the
// list is restated here and the test pins it equal to the contract in both
// directions, aliases included.
//
// WHAT THE BROWSER DECLARES. `POST /v1/files` signs the declared type into the
// presigned PUT and `store()` checks the bytes against it (§2.29's
// `verifyBytes`), so the declaration is a claim the server tests, never a
// guess it trusts. A browser names a DWG drawing with an empty type, or with
// `application/octet-stream`, because no operating system registers the
// format. §4.2 rules that a file named `.dwg` in any case with such a type is
// declared `application/acad`, so the byte check decides. The same rule
// applies to every listed extension a browser cannot type, and Windows with
// Excel installed types a `.csv` file `application/vnd.ms-excel`, a legacy
// Office type the purpose refuses, so a `.csv` name with that type is
// declared `text/csv` and the strict text check of §2.29 judges the bytes.
// Any other declaration is sent as the browser typed it, and a type the list
// does not hold is refused here before a byte is read, with the sentence the
// server's own refusal would earn.
//
// AN ARCHIVE IS A TYPE THAT IS NOT ACCEPTED (§4.2). ZIP, RAR, 7z, gzip and tar
// are refused as any other unlisted type is, because an archive carries any
// type past the byte check (D-335 ruling 2). The sentence names the accepted
// types and never suggests zipping a file.
//
// THE CATEGORY STARTS ON `commercial`, AND ON `technical` FOR A DRAWING (D-335
// rulings 1 and 2). A contract or a price agreement is the cautious default,
// because a `commercial` file is listed only to a holder of
// `sales.read_value` (§7 narrowing 19). A drawing serves inspection and
// packing, so it starts on `technical`. The uploader may change either.

import { RequestError } from "../../../../../lib/request";

/** The `sales_attachment` stored types, `SALES_ATTACHMENT_CONTENT_TYPES` in
 * the contract (pinned equal by the test). */
export const ATTACHMENT_CONTENT_TYPES = [
  "application/pdf",
  "image/png",
  "image/jpeg",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "text/csv",
  "text/plain",
  "application/acad",
] as const;

export type AttachmentContentType = (typeof ATTACHMENT_CONTENT_TYPES)[number];

/** The declared types `POST /v1/files` rewrites before it presigns,
 * `SALES_ATTACHMENT_CONTENT_TYPE_ALIASES` in the contract (pinned equal). */
export const ATTACHMENT_CONTENT_TYPE_ALIASES: Readonly<Record<string, AttachmentContentType>> = {
  "image/vnd.dwg": "application/acad",
};

/** The drawing's one stored type (D-335 ruling 2). */
export const DWG_CONTENT_TYPE: AttachmentContentType = "application/acad";

/** The byte cap, `SALES_ATTACHMENT_MAX_BYTES` in the contract (pinned). */
export const ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024;

/** The longest title, in Unicode code points after trim (§2.29). */
export const ATTACHMENT_TITLE_MAX = 200;

/** Each listed type's extensions, the first being the one a download is named
 * with (`nameFromVerifiedType`, §2.29). The file input's `accept` and the
 * typing of an untyped file both read this one table. */
export const ATTACHMENT_EXTENSIONS: Readonly<Record<AttachmentContentType, readonly string[]>> = {
  "application/pdf": ["pdf"],
  "image/png": ["png"],
  "image/jpeg": ["jpg", "jpeg"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": ["xlsx"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": ["docx"],
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": ["pptx"],
  "text/csv": ["csv"],
  "text/plain": ["txt"],
  "application/acad": ["dwg"],
};

/** The types a browser gives a file it cannot name. A file carrying one of
 * them is typed from its extension, so the byte check decides (§4.2). */
const UNTYPED: ReadonlySet<string> = new Set(["", "application/octet-stream"]);

/** Excel's registration for `.csv` on Windows: a legacy Office type the
 * purpose refuses, carried by plain comma-separated text. */
const EXCEL_CSV_TYPE = "application/vnd.ms-excel";

export type AttachmentCategory = "technical" | "commercial";

/** The `accept` attribute of the file input: every listed extension and every
 * listed type, so the operating system's picker offers what the server
 * stores. It filters the picker and decides nothing; `declaredType` does. */
export function attachmentAccept(): string {
  const extensions = Object.values(ATTACHMENT_EXTENSIONS)
    .flat()
    .map((ext) => `.${ext}`);
  return [...extensions, ...ATTACHMENT_CONTENT_TYPES].join(",");
}

/** The file name's last extension in lower case, or "" when it has none. */
export function extensionOf(name: string): string {
  const dot = name.lastIndexOf(".");
  if (dot <= 0 || dot === name.length - 1) return "";
  return name.slice(dot + 1).toLowerCase();
}

/** The type the extension names, when it names a listed one. */
function typeForExtension(ext: string): AttachmentContentType | undefined {
  for (const type of ATTACHMENT_CONTENT_TYPES) {
    if (ATTACHMENT_EXTENSIONS[type].includes(ext)) return type;
  }
  return undefined;
}

/**
 * The type the upload declares for this file. The browser's own type, bare
 * and lower-cased, unless it is one the browser gives a file it cannot name
 * (empty, `application/octet-stream`) or Excel's `.csv` registration, in
 * which case the listed extension names it. A name with no listed extension
 * keeps the browser's type, which `uploadProblem` then refuses.
 */
export function declaredType(file: { readonly name: string; readonly type: string }): string {
  const bare = (file.type.split(";")[0] ?? "").trim().toLowerCase();
  const ext = extensionOf(file.name);
  if (UNTYPED.has(bare)) return typeForExtension(ext) ?? bare;
  if (bare === EXCEL_CSV_TYPE && ext === "csv") return "text/csv";
  return bare;
}

/** The stored type a declaration resolves to: an alias becomes its stored
 * type, a listed type is itself, and anything else is not accepted. */
export function storedTypeOf(declared: string): AttachmentContentType | undefined {
  const alias = ATTACHMENT_CONTENT_TYPE_ALIASES[declared];
  if (alias !== undefined) return alias;
  return (ATTACHMENT_CONTENT_TYPES as readonly string[]).includes(declared)
    ? (declared as AttachmentContentType)
    : undefined;
}

/** Is this file a drawing, by its declaration or by its name? */
export function isDrawing(file: { readonly name: string; readonly type: string }): boolean {
  return storedTypeOf(declaredType(file)) === DWG_CONTENT_TYPE || extensionOf(file.name) === "dwg";
}

/** The category the upload starts on for this file (D-335 rulings 1 and 2). */
export function defaultCategory(file: {
  readonly name: string;
  readonly type: string;
}): AttachmentCategory {
  return isDrawing(file) ? "technical" : "commercial";
}

/** Why the browser refuses this file before it reads a byte, if it does: a
 * type the purpose does not store (an archive among them), an empty file the
 * registry cannot take (`byte_size` is positive), or one over the cap. */
export type UploadProblem = "type" | "empty" | "too_large";

export function uploadProblem(file: {
  readonly name: string;
  readonly type: string;
  readonly size: number;
}): UploadProblem | undefined {
  if (storedTypeOf(declaredType(file)) === undefined) return "type";
  if (file.size <= 0) return "empty";
  if (file.size > ATTACHMENT_MAX_BYTES) return "too_large";
  return undefined;
}

/** The title as the attach sends it: trimmed, and absent when nothing is
 * left, which the server stores as null either way. */
export function titleForSend(draft: string): string | undefined {
  const trimmed = draft.trim();
  return trimmed.length === 0 ? undefined : trimmed;
}

/** Is the typed title within §2.29's 200 code points after trim? */
export function titleFits(draft: string): boolean {
  return Array.from(draft.trim()).length <= ATTACHMENT_TITLE_MAX;
}

/** The short name of a stored type, the format's own abbreviation ("PDF",
 * "XLSX", "DWG"). A proper noun in every language, so it is not a dictionary
 * value; an unlisted type (a row stored before a list change) reads as its
 * subtype. */
export function typeLabel(contentType: string): string {
  const listed = (ATTACHMENT_CONTENT_TYPES as readonly string[]).includes(contentType)
    ? ATTACHMENT_EXTENSIONS[contentType as AttachmentContentType][0]
    : undefined;
  if (listed !== undefined) return listed.toUpperCase();
  const subtype = contentType.split("/")[1] ?? contentType;
  return subtype.toUpperCase();
}

/** A byte count in the reader's locale, in the unit that keeps it short.
 * The unit is the SI abbreviation the Intl formatter prints in each locale
 * ("1,2 МБ" in Russian), so no dictionary value is needed. */
export function formatSize(bytes: number, locale: string): string {
  const units = [
    { unit: "megabyte", size: 1024 * 1024 },
    { unit: "kilobyte", size: 1024 },
  ] as const;
  for (const { unit, size } of units) {
    if (bytes >= size) {
      return new Intl.NumberFormat(locale, {
        style: "unit",
        unit,
        unitDisplay: "short",
        maximumFractionDigits: 1,
      }).format(bytes / size);
    }
  }
  return new Intl.NumberFormat(locale, {
    style: "unit",
    unit: "byte",
    unitDisplay: "short",
  }).format(bytes);
}

/** Lowercase-hex SHA-256 of a file: what `POST /v1/files` declares and
 * `store()` verifies. */
export async function sha256Hex(blob: Blob): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", await blob.arrayBuffer());
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

// ---------------------------------------------------------------------------
// Refusals, in the reader's words (D-225)
// ---------------------------------------------------------------------------

/** The registry's and the attach's refusals the panel names in words. Every
 * other refusal reads the panel's plain sentence with the server's own title
 * beside it (`MutationFailureNote`). */
const NAMED_REFUSALS: ReadonlySet<string> = new Set([
  "FILE_INTEGRITY_MISMATCH",
  "FILE_CONTENT_TYPE_NOT_ALLOWED",
  "FILE_TOO_LARGE",
  "FILE_NOT_STORED",
  "ATTACHMENT_FILE_NOT_YOURS",
  "ATTACHMENT_ALREADY_LINKED",
  "ATTACHMENT_FILE_WRONG_PURPOSE",
  "ATTACHMENT_NOT_FOUND",
  "CUSTOMER_ARCHIVED",
  "CUSTOMER_NOT_FOUND",
  "QUOTE_NOT_FOUND",
  "ORDER_NOT_FOUND",
]);

/**
 * The i18n key naming an upload's, an attach's or a detach's refusal, if the
 * panel names it. A 422 `CUSTOMER_ARCHIVED` that carries
 * `merged_into_customer_id` is the merged customer's refusal, which names the
 * winner (`mergedWinnerOf`). A 403 without a named code is the role's.
 */
export function attachmentRefusalKey(error: unknown): string | undefined {
  if (!(error instanceof RequestError)) return undefined;
  if (error.code === "CUSTOMER_ARCHIVED" && mergedWinnerOf(error) !== undefined) {
    return "sales.attachments.refusal.CUSTOMER_MERGED";
  }
  if (error.code !== undefined && NAMED_REFUSALS.has(error.code)) {
    return `sales.attachments.refusal.${error.code}`;
  }
  if (error.httpStatus === 403) return "sales.attachments.refusal.forbidden";
  return undefined;
}

/** The winner a 422 `CUSTOMER_ARCHIVED` names when the customer was merged
 * (`merged_into_customer_id`, a top-level member of the problem body, as
 * every extension here is), or undefined. */
export function mergedWinnerOf(error: unknown): string | undefined {
  if (!(error instanceof RequestError) || error.code !== "CUSTOMER_ARCHIVED") return undefined;
  const problem = error.problem as Record<string, unknown> | undefined;
  const winner = problem?.["merged_into_customer_id"];
  return typeof winner === "string" && winner.length > 0 ? winner : undefined;
}

/** Start the browser's download of a minted URL. The door serves the object
 * with `attachment` disposition, so the page stays where it is. The anchor
 * sits in the document for the click, as a detached click is ignored by some
 * browsers. */
export function startDownload(url: string, doc: Document = document): void {
  const anchor = doc.createElement("a");
  anchor.href = url;
  anchor.rel = "noreferrer";
  anchor.style.display = "none";
  doc.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}
